import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib/components/selects/CustomSelect/CustomSelect";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import { CheckmarkOption } from "@smartsuite/react-ui/lib/components/selects/components/CheckmarkOption/CheckmarkOption";
import { memo, useCallback, useEffect, useMemo } from "react";
import { hasTextAttrs } from "../../canvas/state/attributes";
import { DocumentDesignItem } from "../../canvas/state/types";
import { selectableFamilies } from "../../fonts/font-registry";
import { showElementWithoutImplementation } from "../utils";

export interface FontFamilySelectorFieldProps {
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

const FontFamilySelectorFieldRaw: React.FC<FontFamilySelectorFieldProps> = ({
  selected,
  onChangeSelected,
}) => {
  const [fontFamilySelectState, setFontFamilySelectState] = useSelectState();

  // Offer only fonts the registry can reproduce in both preview and PDF, so the
  // list is deterministic instead of depending on which fonts the viewer's OS
  // happens to have installed.
  const fontFamilyOptions = useMemo(
    () =>
      selectableFamilies.map((family) => ({ value: family, label: family })),
    [],
  );

  const fontFamilyOptionsSet = useMemo(() => new Set(selectableFamilies), []);

  useEffect(() => {
    if (
      hasTextAttrs(selected) &&
      selected.fontFamily &&
      !fontFamilyOptionsSet.has(selected.fontFamily)
    ) {
      onChangeSelected({ ...selected, fontFamily: "Arial" });
    }
  }, [selected, onChangeSelected, fontFamilyOptionsSet]);

  const getItemFontFamily = useCallback(
    (fontFamily: string): string => {
      return fontFamilyOptionsSet.has(fontFamily) ? fontFamily : "Arial";
    },
    [fontFamilyOptionsSet],
  );

  // Renders the font name styled in the font itself, for an at-a-glance preview.
  const renderStylizedText = (
    label: string,
    value: string,
  ): React.ReactNode => <Text styles={{ fontFamily: value }}>{label}</Text>;

  return (
    <>
      {hasTextAttrs(selected) && (
        <div>
          <Text
            weight="medium"
            color={fontFamilySelectState ? "link" : "primary"}
            wrapMode="ellipsis"
            styles={{ marginBottom: 4 }}
          >
            Font
          </Text>
          {showElementWithoutImplementation && (
            <div className="sidebar-selection-config__button-config">
              <button>Preview fonts (?)</button>
              <button>Set as default font (TODO)</button>
            </div>
          )}
          <KeyValueCustomSelect<"value", "label", (typeof fontFamilyOptions)[0]>
            state={fontFamilySelectState}
            className="font-family-selector-field__select"
            options={fontFamilyOptions}
            showClear={false}
            placeholderArrowStyle="filled-dark"
            value={getItemFontFamily(selected.fontFamily)}
            showFilterGreaterThan={4}
            searchPlaceholder="Search for a font"
            renderOption={({ label, value }, active) => (
              <CheckmarkOption rightAlignSelected={true} selected={active}>
                {renderStylizedText(label, value)}
              </CheckmarkOption>
            )}
            renderSelectedOption={({ label, value }) =>
              renderStylizedText(label, value)
            }
            onChange={(value) =>
              onChangeSelected({ ...selected, fontFamily: value })
            }
            onOpenedChange={setFontFamilySelectState}
          />
        </div>
      )}
    </>
  );
};

export const FontFamilySelectorField = memo(FontFamilySelectorFieldRaw);
